import { useMutation } from '@apollo/client';
import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateRiskAssessmentResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateRiskAssessmentResultDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateRiskAssessmentResultTRPC } from './useUpdateRiskAssessmentResultTRPC';

type UpdateRiskAssessmentResultInput = {
  Id: string;
  Rating?: number | null;
  Likelihood?: number | null;
  Impact?: number | null;
  Impacts?: { Label: string; Value: number }[] | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  AssessmentId?: string | null;
  OriginalTimestamp: string;
  ParentRiskId?: string | null;
};

export const useUpdateRiskAssessmentResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateRiskAssessmentResultGraphQL, graphqlState] = useMutation(
    UpdateRiskAssessmentResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk_assessment_result');
        evictField(cache, 'assessment');
        evictField(cache, 'risk_assessment_result_aggregate');
      },
      refetchQueries: [namedOperations.Query.getRiskScoresByRiskId],
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateRiskAssessmentResultTRPC();

  const updateRiskAssessmentResult = async (
    variables: UpdateRiskAssessmentResultInput
  ): Promise<UpdateRiskAssessmentResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateRiskAssessmentResult(variables);
    }

    const result = await updateRiskAssessmentResultGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to update risk assessment result');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateRiskAssessmentResult,
      isLoading: trpcMutation.isLoading,
    };
  }

  return {
    updateRiskAssessmentResult,
    isLoading: graphqlState.loading,
  };
};
